/*
 * Copyright (c) 2024-2026. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import clsx from 'clsx'
import {Dispatch, SetStateAction} from 'react'
import {useTranslation} from 'react-i18next'
import {Controller, FieldErrors, UseFormRegister, UseFormSetValue, Control} from 'react-hook-form'
import UiFormInner from 'components/Forms/UIFormInner'
import UiFormGrid from 'components/Forms/UiFormGrid'
import UiFormField from 'components/Forms/UiFormField'
import CompanyFormInput from 'pages/Front/Booking/components/CompanyForm/CompanyFormInput'
import CompanyAutocomplete from 'components/CompanyAutocomplete'
import CompanyOriginSelect from 'components/Companies/forms/CompanyOriginSelect'
import LeadTagsAutocomplete from 'components/leads/forms/LeadTagsAutocomplete'
import UiSellerSelect from 'components/leads/UiSellerSelect'
import UiSelectWithGraphics from 'components/Ui/UiSelectWithGraphics'
import {Company} from 'types/entities/Company'
import {CompanyBranchType} from 'types/enums/CompanyBranchType'
import {CompanyBasicInputs} from 'components/Companies/schemas/useCompanyBasicSchema'
import {MagnifyingGlassMinusIcon, MagnifyingGlassPlusIcon} from '@heroicons/react/24/solid'
import CompanyBasicDuplicateWarning from './CompanyBasicDuplicateWarning'
import {normalizeSelectableString} from '../CompanyBasicFormUtils'
import {isNonBusinessAccountType} from 'utils/companyUtils'

interface CompanyBasicFormMainContentProps {
    compact: boolean
    register: UseFormRegister<CompanyBasicInputs>
    control: Control<CompanyBasicInputs>
    errors: FieldErrors<CompanyBasicInputs>
    setValue: UseFormSetValue<CompanyBasicInputs>
    duplicates: Company[]
    isInEditModal: boolean
    isOpeningMergeModal: boolean
    showDuplicatesPopover: boolean
    handleMouseEnter: () => void
    handleMouseLeave: () => void
    handleOpenMergeModal: () => void
    onDuplicateClick?: (company: Company) => void
    selectedBusinessBranchType: CompanyBranchType
    parentCompany: Company | null
    setParentCompany: (company: Company | null) => void
    businessCategoryOptionsUi: Array<{ label: string; description?: string; value: string | number }>
    selectedBusinessCategory: string | number | null
    setSelectedBusinessCategory: (value: string | number | null) => void
    businessBranchTypeOptionsUi: Array<{ label: string; description?: string; value: string | number }>
    setSelectedBusinessBranchType: (value: string | number | null) => void
    employeeSizeOptionsUi: Array<{ label: string; description?: string; value: string | number }>
    selectedEmployeeSize: string | number | null
    setSelectedEmployeeSize: (value: string | number | null) => void
    notesFontSize: number
    setNotesFontSize: Dispatch<SetStateAction<number>>
}

interface NotesFontControlsProps {
    onDecrease: () => void
    onIncrease: () => void
    decreaseTitle: string
    increaseTitle: string
}

const NotesFontControls = ({ onDecrease, onIncrease, decreaseTitle, increaseTitle }: NotesFontControlsProps) => {
    return (
        <div className="flex items-center gap-1">
            <button
                type="button"
                onClick={onDecrease}
                className="p-1 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-md transition-colors"
                title={decreaseTitle}
            >
                <MagnifyingGlassMinusIcon className="w-3.5 h-3.5 text-gray-500" />
            </button>
            <button
                type="button"
                onClick={onIncrease}
                className="p-1 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-md transition-colors"
                title={increaseTitle}
            >
                <MagnifyingGlassPlusIcon className="w-3.5 h-3.5 text-gray-500" />
            </button>
        </div>
    )
}

const CompanyBasicFormMainContent = ({
    compact,
    register,
    control,
    errors,
    setValue,
    duplicates,
    isInEditModal,
    isOpeningMergeModal,
    showDuplicatesPopover,
    handleMouseEnter,
    handleMouseLeave,
    handleOpenMergeModal,
    onDuplicateClick,
    selectedBusinessBranchType,
    parentCompany,
    setParentCompany,
    businessCategoryOptionsUi,
    selectedBusinessCategory,
    setSelectedBusinessCategory,
    businessBranchTypeOptionsUi,
    setSelectedBusinessBranchType,
    employeeSizeOptionsUi,
    selectedEmployeeSize,
    setSelectedEmployeeSize,
    notesFontSize,
    setNotesFontSize,
}: CompanyBasicFormMainContentProps) => {
    const { t } = useTranslation()
    const notesInputStyle = { fontSize: `${notesFontSize}px !important` }
    const isCompanyFieldsDisabled = isNonBusinessAccountType(selectedBusinessBranchType)
    const disabledCompanyFieldsTitle = isCompanyFieldsDisabled
        ? t('These fields are not available for school, club, or private accounts')
        : undefined

    const clearNonBusinessCompanyFields = () => {
        setSelectedEmployeeSize(null)
        setValue('employee_size' as keyof CompanyBasicInputs, null, { shouldValidate: false })
        setValue('employee_count' as keyof CompanyBasicInputs, null, { shouldValidate: false })
        setValue('cin' as keyof CompanyBasicInputs, '', { shouldValidate: false })
        setValue('vat_number' as keyof CompanyBasicInputs, '', { shouldValidate: false })
    }

    return (
        <UiFormInner className="CompanyBasicFormMainContent">
            <div className={clsx(compact ? 'flex flex-col' : 'flex flex-col lg:flex-row gap-6')}>
                <div className={clsx('flex-1')}>
                    <UiFormGrid
                        cols={compact ? 1 : 2}
                        gap={compact ? 3 : 4}
                        responsive={false}
                        className={clsx(compact ? 'grid-cols-2' : 'grid-cols-2')}
                    >
                        <div className={clsx('col-span-2 flex flex-col gap-2')}>
                            <div className={clsx('flex-1 w-full')}>
                                <div className={clsx('flex items-center justify-between mb-2')}>
                                    <label className={clsx(
                                        'font-medium text-gray-700 dark:text-gray-200 text-sm block select-none'
                                    )}>
                                        {t('Company name')}
                                        {selectedBusinessBranchType !== CompanyBranchType.Private && <span className="text-red-500"> *</span>}
                                    </label>
                                    {isInEditModal && (
                                        <CompanyBasicDuplicateWarning
                                            duplicates={duplicates}
                                            isOpeningMergeModal={isOpeningMergeModal}
                                            showDuplicatesPopover={showDuplicatesPopover}
                                            onMouseEnter={handleMouseEnter}
                                            onMouseLeave={handleMouseLeave}
                                            onClick={handleOpenMergeModal}
                                            onDuplicateClick={onDuplicateClick}
                                        />
                                    )}
                                </div>
                                <CompanyFormInput
                                    className={clsx('flex-1')}
                                    label={null}
                                    name="name"
                                    register={register}
                                    errors={errors}
                                    iconName="company_name"
                                    containerClassName="relative"
                                    required={false}
                                    placeholder={t('Enter company name')}
                                />
                            </div>
                        </div>

                        <UiFormField className={clsx('col-span-1')} label={t('Seller')}>
                            <Controller
                                control={control}
                                name="seller_id"
                                render={({ field }) => (
                                    <UiSellerSelect
                                        selected={field.value ?? null}
                                        setSelected={(val) =>
                                            field.onChange(val != null && val !== '' ? Number(val) : null)
                                        }
                                        autoSelectCurrentUser={!isInEditModal}
                                    />
                                )}
                            />
                        </UiFormField>

                        <UiFormField className={clsx('col-span-1')} error={errors.type?.message}>
                            <UiSelectWithGraphics
                                label={t('Account type')}
                                options={businessBranchTypeOptionsUi}
                                selected={selectedBusinessBranchType}
                                dropdownDirection={'up'}
                                setSelected={(value: string | number | null) => {
                                    const next = (value ?? CompanyBranchType.Company) as CompanyBranchType
                                    setSelectedBusinessBranchType(next)
                                    setValue('type' as keyof CompanyBasicInputs, next as string, {
                                        shouldValidate: true,
                                    })
                                    // Clear branch/parent fields when switching away from Account type
                                    if (next !== CompanyBranchType.Branch) {
                                        setValue('branch' as keyof CompanyBasicInputs, null, { shouldValidate: false })
                                        setValue('parent_company_id' as keyof CompanyBasicInputs, null, { shouldValidate: false })
                                        setParentCompany(null)
                                    }
                                    if (isNonBusinessAccountType(next)) {
                                        clearNonBusinessCompanyFields()
                                    }
                                }}
                            />
                        </UiFormField>

                        <UiFormField
                            className={clsx(compact ? 'col-span-2' : 'col-span-2')}
                            error={errors.business_category?.message}
                        >
                            <UiSelectWithGraphics
                                label={t('Business Category')}
                                options={businessCategoryOptionsUi}
                                selected={selectedBusinessCategory}
                                dropdownDirection={'up'}
                                setSelected={(value: string | number | null) => {
                                    const next = normalizeSelectableString(value)
                                    setSelectedBusinessCategory(next)
                                    setValue('business_category' as keyof CompanyBasicInputs, next, {
                                        shouldValidate: true,
                                    })
                                }}
                            />
                        </UiFormField>

                        <UiFormField
                            className={clsx(compact ? 'col-span-2' : 'col-span-2')}
                            error={errors.employee_size?.message}
                        >
                            <UiSelectWithGraphics
                                label={t('Employee Size')}
                                options={employeeSizeOptionsUi}
                                selected={selectedEmployeeSize}
                                dropdownDirection={'up'}
                                disabled={isCompanyFieldsDisabled}
                                title={disabledCompanyFieldsTitle}
                                setSelected={(value: string | number | null) => {
                                    const next = normalizeSelectableString(value)
                                    setSelectedEmployeeSize(next)
                                    setValue('employee_size' as keyof CompanyBasicInputs, next, {
                                        shouldValidate: true,
                                    })
                                }}
                            />
                        </UiFormField>

                        <CompanyFormInput
                            className={clsx(compact ? 'col-span-2' : 'col-span-2')}
                            label={t('Employee count')}
                            name="employee_count"
                            register={register}
                            errors={errors}
                            placeholder={t('Enter employee count')}
                            disabled={isCompanyFieldsDisabled}
                            title={disabledCompanyFieldsTitle}
                        />

                        {selectedBusinessBranchType === CompanyBranchType.Branch && (
                            <div className={clsx('col-span-2 flex flex-col gap-3')}>
                                <CompanyFormInput
                                    className={clsx('w-full')}
                                    label={t('Branch name')}
                                    name="branch"
                                    register={register}
                                    errors={errors}
                                    placeholder={t('Enter branch name')}
                                />
                                <CompanyAutocomplete
                                    className={clsx('w-full')}
                                    label={t('Parent Company')}
                                    name="parent_company"
                                    placeholder={t('Search for parent company...')}
                                    onSelect={(val) => {
                                        setParentCompany(val)
                                        setValue('parent_company_id' as keyof CompanyBasicInputs, val?.id || null, {
                                            shouldValidate: true,
                                        })
                                    }}
                                    defaultValue={parentCompany}
                                    allowCreate={true}
                                    autoCreate={true}
                                />
                            </div>
                        )}

                        <CompanyFormInput
                            className={clsx(compact ? 'col-span-2' : 'col-span-2')}
                            label={t('CIN Number')}
                            name="cin"
                            register={register}
                            errors={errors}
                            placeholder={t('Enter CIN')}
                            disabled={isCompanyFieldsDisabled}
                            title={disabledCompanyFieldsTitle}
                        />

                        <CompanyFormInput
                            className={clsx(compact ? 'col-span-2' : 'col-span-2')}
                            label={t('VAT Number')}
                            name="vat_number"
                            register={register}
                            errors={errors}
                            placeholder={t('Enter VAT')}
                            disabled={isCompanyFieldsDisabled}
                            title={disabledCompanyFieldsTitle}
                        />

                        <CompanyFormInput
                            className={clsx(compact ? 'col-span-2' : 'col-span-1')}
                            label={t('Referral ID')}
                            name="referral_id"
                            register={register}
                            errors={errors}
                            placeholder={t('Enter referral ID')}
                        />

                        <CompanyFormInput
                            className={clsx(compact ? 'col-span-2' : 'col-span-1')}
                            label={t('Website')}
                            name="website"
                            register={register}
                            errors={errors}
                            placeholder="https://example.com"
                        />

                        <Controller
                            control={control}
                            name="origin"
                            render={({field}) => (
                                <CompanyOriginSelect
                                    className={clsx(compact ? 'col-span-2' : 'col-span-2')}
                                    value={field.value || null}
                                    onChange={(val) => field.onChange(val ?? '')}
                                    error={errors.origin?.message}
                                    errors={errors}
                                />
                            )}
                        />

                        <UiFormField
                            className={clsx(compact ? 'col-span-2' : 'col-span-2')}
                            label={t('Tags')}
                            error={
                                errors.tags
                                    ? Array.isArray(errors.tags)
                                        ? (errors.tags as {message?: string}[])[0]?.message
                                        : errors.tags.message
                                    : undefined
                            }
                        >
                            <Controller
                                control={control}
                                name="tags"
                                render={({field}) => (
                                    <LeadTagsAutocomplete
                                        selectedTags={field.value || []}
                                        onTagsChange={(newTags) => field.onChange(newTags)}
                                        placeholder={t('Type to search or add tags...')}
                                    />
                                )}
                            />
                        </UiFormField>

                        <CompanyFormInput
                            className={clsx(compact ? 'col-span-2' : 'col-span-2')}
                            label={
                                compact ? (
                                    <div className="flex items-center justify-between">
                                        <span>{t('Account Notes')}</span>
                                        <NotesFontControls
                                            onDecrease={() => setNotesFontSize((prev) => Math.max(10, prev - 1))}
                                            onIncrease={() => setNotesFontSize((prev) => Math.min(24, prev + 1))}
                                            decreaseTitle={t('Decrease font size')}
                                            increaseTitle={t('Increase font size')}
                                        />
                                    </div>
                                ) : (
                                    t('Account Notes')
                                )
                            }
                            name="notes"
                            register={register}
                            errors={errors}
                            placeholder={t('Add any additional notes...')}
                            component="textarea"
                            inputStyle={notesInputStyle}
                        />
                    </UiFormGrid>
                </div>
            </div>
        </UiFormInner>
    )
}

export default CompanyBasicFormMainContent
